
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Users, Clock, MenuIcon, Calendar } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { startOfDay, isToday } from 'date-fns';
import { InputNumber } from '@/components/ui/input-number';

const TablesPage: React.FC = () => {
  const navigate = useNavigate();
  const { tables, createNewTable } = useApp();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showAllTables, setShowAllTables] = useState(false);
  const [selectedTableNumber, setSelectedTableNumber] = useState(1);
  const isMobile = useIsMobile();
  
  // Filtra as mesas do dia atual e remove mesas canceladas
  const filteredTables = useMemo(() => {
    // Primeiro filtramos as mesas canceladas, independente da opção "mostrar todas"
    const activeTables = tables.filter(table => table.status !== 'cancelled');
    
    let tablesToShow;
    
    if (showAllTables) {
      tablesToShow = activeTables;
    } else {
      tablesToShow = activeTables.filter(table => {
        // Se a mesa foi criada hoje
        if (table.createdAt && isToday(new Date(table.createdAt))) {
          return true;
        }
        
        // Se a mesa foi aberta hoje
        if (table.timeOpened && isToday(new Date(table.timeOpened))) {
          return true;
        }
        
        // Se a mesa tem pedidos de hoje
        if (table.orders && table.orders.some(order => 
          order.createdAt && isToday(new Date(order.createdAt))
        )) {
          return true;
        }
        
        // Para mesas sem nenhuma data registrada, verificamos o status
        // Mesas disponíveis sempre são mostradas
        return table.status === 'available';
      });
    }
    
    // Organizamos as mesas em ordem crescente pelo número
    return tablesToShow.sort((a, b) => a.number - b.number);
  }, [tables, showAllTables]);
  
  // Função para obter números de mesa já em uso
  const getUsedTableNumbers = () => {
    return tables
      .filter(table => table.status !== 'cancelled')
      .map(table => table.number);
  };
  
  // Função para encontrar o próximo número de mesa disponível
  const findNextAvailableTableNumber = () => {
    const usedNumbers = getUsedTableNumbers();
    
    if (usedNumbers.length === 0) return 1;
    
    // Encontrar o primeiro número disponível entre 1 e 50
    for (let i = 1; i <= 50; i++) {
      if (!usedNumbers.includes(i)) {
        return i;
      }
    }
    
    return 1; // Fallback para 1 se todos os números estiverem ocupados
  };
  
  // Ao abrir o diálogo, selecionar automaticamente o próximo número disponível
  const handleOpenDialog = () => {
    setSelectedTableNumber(findNextAvailableTableNumber());
    setIsDialogOpen(true);
  };

  const handleAddTable = () => {
    // Create a new table using the createNewTable method from context
    const defaultSeats = 4; // Default value for seats
    
    // Verificamos se o número já está em uso
    const usedNumbers = getUsedTableNumbers();
    if (usedNumbers.includes(selectedTableNumber)) {
      // Se estiver em uso, encontramos o próximo disponível
      const nextAvailable = findNextAvailableTableNumber();
      setSelectedTableNumber(nextAvailable);
      
      // Criamos a mesa com o número disponível
      const newTable = createNewTable(nextAvailable, defaultSeats);
      
      // Atualiza a data de criação da mesa
      const today = new Date();
      if (newTable) {
        newTable.createdAt = today;
      }
    } else {
      // Se não estiver em uso, usamos o número selecionado
      const newTable = createNewTable(selectedTableNumber, defaultSeats);
      
      // Atualiza a data de criação da mesa
      const today = new Date();
      if (newTable) {
        newTable.createdAt = today;
      }
    }
    
    setIsDialogOpen(false);
  };

  const getTableStatusClass = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'occupied':
        return 'bg-rose-100 text-rose-800 border-rose-200';
      case 'reserved':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'cancelled':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  const getTableStatus = (status: string) => {
    switch (status) {
      case 'available':
        return 'Disponível';
      case 'occupied':
        return 'Ocupada';
      case 'reserved':
        return 'Reservada';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  };
  
  const getTableColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'border-emerald-200 bg-gradient-to-br from-white to-emerald-50';
      case 'occupied':
        return 'border-rose-200 bg-gradient-to-br from-white to-rose-50';
      case 'reserved':
        return 'border-blue-200 bg-gradient-to-br from-white to-blue-50';
      case 'cancelled':
        return 'border-amber-200 bg-gradient-to-br from-white to-amber-50';
      default:
        return 'border-gray-200';
    }
  };

  return (
    <div className="container py-4 md:py-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl md:text-2xl font-bold">Mesas</h1>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowAllTables(!showAllTables)} 
            variant="outline" 
            size={isMobile ? "sm" : "default"} 
            className="shadow-sm"
          >
            <Calendar className="mr-1 h-4 w-4" />
            {showAllTables ? "Mostrar do Dia" : "Mostrar Todas"}
          </Button>
          <Button onClick={handleOpenDialog} size={isMobile ? "sm" : "default"} className="shadow-sm">
            <Plus className="mr-1 h-4 w-4" />
            {!isMobile && "Adicionar Mesa"}
            {isMobile && "Mesa"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {filteredTables.length > 0 ? (
          filteredTables.map((table) => (
            <Card 
              key={table.id} 
              className={`cursor-pointer hover:shadow-lg transition-all duration-200 table-item border-2 ${getTableColor(table.status)}`} 
              onClick={() => navigate(`/tables/${table.id}`)}
            >
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="flex justify-between items-center">
                  <span>Mesa {table.number}</span>
                  <Badge className={`w-fit ${getTableStatusClass(table.status)}`}>
                    {getTableStatus(table.status)}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-2">
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center text-gray-600">
                    <MenuIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm">
                      {table.orders.length} {table.orders.length === 1 ? 'Pedido' : 'Pedidos'}
                    </span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Users className="h-4 w-4 mr-2" />
                    <span className="text-sm">{table.seats} Lugares</span>
                  </div>
                  {table.timeOpened && (
                    <div className="flex items-center text-gray-600">
                      <Clock className="h-4 w-4 mr-2" />
                      <span className="text-sm">Aberta às {new Date(table.timeOpened).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="pt-0">
                <Button variant="outline" size={isMobile ? "sm" : "default"} className="w-full" onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/tables/${table.id}`);
                }}>
                  Ver Detalhes
                </Button>
              </CardFooter>
            </Card>
          ))
        ) : (
          <div className="col-span-full text-center py-10 text-gray-500">
            <MenuIcon className="mx-auto h-10 w-10 mb-2" />
            <p>Nenhuma mesa encontrada para o dia atual</p>
            {!showAllTables && (
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-4" 
                onClick={() => setShowAllTables(true)}
              >
                Mostrar Todas as Mesas
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Dialog for adding table */}
      {isDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 md:p-6 rounded-md shadow-lg max-w-sm w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">Adicionar Nova Mesa</h2>
            <div className="mb-4">
              <label htmlFor="table-number" className="block text-sm font-medium text-gray-700 mb-1">
                Número da Mesa (1-50)
              </label>
              <div className="flex items-center">
                <InputNumber
                  id="table-number"
                  min={1}
                  max={50}
                  value={selectedTableNumber}
                  onChange={setSelectedTableNumber}
                  className="w-full"
                />
              </div>
              {getUsedTableNumbers().includes(selectedTableNumber) && (
                <p className="text-yellow-600 text-sm mt-1">
                  Este número já está em uso. Será selecionado o próximo disponível.
                </p>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button className="shadow-sm" onClick={handleAddTable}>
                Adicionar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TablesPage;
